import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  CircularProgress,
  Drawer,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  LegendItem,
  MONO_FONT,
  PAGE_FONT,
  PillTabs,
  headCellSx,
  numCellSx,
  textCellSx,
} from "../../components/metrics/metricsUi";
import ProductPicture from "../../components/product-image/ProductPicture";
import { isMobile } from "../../globalStyles/mobile";
import { inventoryRiskService } from "../../utils/services/inventoryRiskService";
import {
  HISTORY_DAY_TABS,
  RISK_BY_KEY,
  buildHistoryParams,
  fmtDays,
  fmtMoney,
  fmtUnits,
  historyCoverageNote,
  inventoryRiskErrorMessage,
  snapshotComparisonParts,
} from "../../utils/inventoryRiskPageDefaults";
import { RiskCounts, RiskLabel, RiskProfileBar } from "./RiskProfile";

const shortDay = (value) =>
  new Date(`${value}T00:00:00.000Z`).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });

const longDay = (value) =>
  value
    ? new Date(`${value}T00:00:00.000Z`).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        timeZone: "UTC",
      })
    : "—";

// Tall enough to pin the history panel and still leave room to scroll the SKU list.
const PIN_HISTORY_QUERY = "(min-height:760px)";
const CHART_HEIGHT = 220;
const PINNED_CHART_HEIGHT = 180;
// The line is daily snapshots taken at the start of each day, not live stock.
const ON_HAND_SERIES = "On hand (start of day)";

/** Fetch with abort + "latest request wins", keyed on `deps`. */
const useLatestRequest = (load, deps) => {
  const [state, setState] = useState({ data: null, loading: false, error: "" });
  const seq = useRef(0);
  useEffect(() => {
    if (!load) {
      seq.current += 1;
      setState({ data: null, loading: false, error: "" });
      return undefined;
    }
    const current = ++seq.current;
    const controller = new AbortController();
    setState({ data: null, loading: true, error: "" });
    load(controller.signal)
      .then((data) => {
        if (current === seq.current) setState({ data, loading: false, error: "" });
      })
      .catch((err) => {
        if (current !== seq.current || controller.signal.aborted) return;
        setState({ data: null, loading: false, error: inventoryRiskErrorMessage(err) });
      });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return state;
};

const Stat = ({ label, value, ui, tone }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography
      sx={{
        fontSize: 10.5,
        fontWeight: 700,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        color: ui.colors.textSecondary,
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        fontFamily: MONO_FONT,
        fontSize: 17,
        fontWeight: 650,
        fontVariantNumeric: "tabular-nums",
        color: tone || ui.colors.textPrimary,
        mt: 0.4,
      }}
    >
      {value}
    </Typography>
  </Box>
);

const Inline = ({ ui, children, tone }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1,
      py: 2,
      fontSize: 12.5,
      color: tone || ui.colors.textSecondary,
    }}
  >
    {children}
  </Box>
);

const SkuHistory = ({ history, ui, chartHeight, liveOnHand }) => {
  const inHandColor = ui.accent;
  const salesColor = ui.darkMode ? "rgba(148,163,184,0.55)" : "#B8C0CC";
  const stockoutColor = RISK_BY_KEY.out_of_stock.color;
  const note = historyCoverageNote(history, longDay);

  return (
    <>
      <Typography sx={{ fontSize: 12, color: ui.colors.textPrimary, mt: 1.25, lineHeight: 1.5 }}>
        {snapshotComparisonParts(history, liveOnHand, longDay).join(" · ")}
      </Typography>
      <Box sx={{ height: chartHeight, mt: 1 }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={history.series} margin={{ top: 8, right: 8, left: -4, bottom: 0 }}>
            <CartesianGrid vertical={false} stroke={ui.colors.border} strokeDasharray="3 3" />
            <XAxis
              dataKey="date"
              tickFormatter={shortDay}
              tick={{ fontSize: 10.5, fill: ui.colors.textSecondary }}
              minTickGap={24}
              tickLine={false}
              axisLine={{ stroke: ui.colors.border }}
            />
            {/* One shared units axis so bar and line heights are directly comparable. */}
            <YAxis
              allowDecimals={false}
              tick={{ fontSize: 10.5, fill: ui.colors.textSecondary }}
              tickLine={false}
              axisLine={false}
              width={52}
              label={{
                value: "Units",
                angle: -90,
                position: "insideLeft",
                offset: 14,
                style: { fontSize: 10.5, fill: ui.colors.textSecondary, textAnchor: "middle" },
              }}
            />
            <RechartsTooltip
              labelFormatter={longDay}
              formatter={(value, name) => [
                value == null ? (name === ON_HAND_SERIES ? "No snapshot" : "No order data") : fmtUnits(value),
                name,
              ]}
              contentStyle={{
                fontSize: 12,
                fontFamily: PAGE_FONT,
                background: ui.colors.bgPaper,
                border: `1px solid ${ui.colors.border}`,
                borderRadius: 8,
              }}
            />
            <Bar
              dataKey="units"
              name="Units sold / day"
              fill={salesColor}
              maxBarSize={10}
              // Keep small non-zero days visible next to a much larger on-hand line.
              minPointSize={(value) => (value > 0 ? 2 : 0)}
            />
            <Line
              dataKey="in_hand"
              name={ON_HAND_SERIES}
              stroke={inHandColor}
              strokeWidth={2}
              dot={false}
              connectNulls={false}
              isAnimationActive={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </Box>
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mt: 1, alignItems: "center" }}>
        <LegendItem color={inHandColor} label={ON_HAND_SERIES} line />
        <LegendItem color={salesColor} label="Units sold / day" />
        <LegendItem color={stockoutColor} label={`Stockout · ${fmtUnits(history.stockout_days)} days`} />
      </Box>
      {note ? (
        <Typography sx={{ fontSize: 11.5, color: ui.colors.textTertiary, mt: 1, lineHeight: 1.5 }}>
          {note}.
        </Typography>
      ) : null}
    </>
  );
};

const ProductRiskDrawer = ({ product, onClose, clientId, warehouseId, warehouseLabel, ui }) => {
  const { colors } = ui;
  const mobile = isMobile();
  const productId = product?.product_id || null;
  const [selectedSku, setSelectedSku] = useState(null);
  const [days, setDays] = useState(30);
  const pinHistory = useMediaQuery(PIN_HISTORY_QUERY);
  const chartHeight = pinHistory ? PINNED_CHART_HEIGHT : CHART_HEIGHT;
  // The drawer paper is the scroll container; the sentinel just below the history panel is
  // off-screen exactly while the panel is stuck, which is when it gets its top shadow.
  const [historyStuck, setHistoryStuck] = useState(false);
  const stuckSentinel = useRef(null);

  useEffect(() => {
    setSelectedSku(null);
  }, [productId, warehouseId]);

  const detail = useLatestRequest(
    productId && warehouseId
      ? (signal) =>
          inventoryRiskService.fetchProduct(
            productId,
            { warehouse_id: warehouseId, ...(clientId ? { client_id: clientId } : {}) },
            { signal },
          )
      : null,
    [productId, warehouseId, clientId],
  );

  // Only an explicitly selected SKU ever loads history.
  const history = useLatestRequest(
    productId && warehouseId && selectedSku
      ? (signal) =>
          inventoryRiskService.fetchHistory(
            buildHistoryParams({ clientId, warehouseId, productId, sku: selectedSku, days }),
            { signal },
          )
      : null,
    [productId, warehouseId, clientId, selectedSku, days],
  );

  useEffect(() => {
    const node = stuckSentinel.current;
    if (!pinHistory || !node || typeof IntersectionObserver === "undefined") {
      setHistoryStuck(false);
      return undefined;
    }
    const observer = new IntersectionObserver(([entry]) => setHistoryStuck(!entry.isIntersecting));
    observer.observe(node);
    return () => observer.disconnect();
  }, [pinHistory, selectedSku]);

  // Show the table row immediately; the drawer response (same cached numbers) replaces it.
  const summary = detail.data?.product || product;
  const skus = detail.data?.skus || [];
  const hidden = detail.data?.hidden_inactive_skus || 0;
  // Live on-hand from the SKU table, shown next to the chart's latest snapshot.
  const selectedOnHand = skus.find((row) => row.sku === selectedSku)?.on_hand;

  return (
    <Drawer
      anchor="right"
      open={Boolean(product)}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: mobile ? "100%" : 580,
          backgroundColor: colors.bgPaper,
          fontFamily: PAGE_FONT,
        },
      }}
    >
      {summary ? (
        <Box sx={{ p: mobile ? 2 : 3 }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 2 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: 11.5, color: colors.textSecondary, mb: 0.4 }}>
                Inventory risk{warehouseLabel ? ` · ${warehouseLabel}` : ""}
              </Typography>
              <Typography
                sx={{ fontSize: 17, fontWeight: 700, color: colors.textPrimary, lineHeight: 1.3 }}
              >
                {summary.product_name || "Unnamed product"}
              </Typography>
            </Box>
            <IconButton size="small" onClick={onClose} aria-label="Close">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          <Box
            sx={{
              ...ui.card,
              p: 2,
              display: "grid",
              gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
              gap: 1.5,
            }}
          >
            <Stat ui={ui} label="Days cover" value={fmtDays(summary.days_cover)} />
            <Stat
              ui={ui}
              label="Rev. at risk"
              value={fmtMoney(summary.revenue_at_risk)}
              tone={summary.revenue_at_risk > 0 ? ui.danger : undefined}
            />
            <Stat ui={ui} label="On hand" value={fmtUnits(summary.on_hand)} />
            <Stat ui={ui} label="30-day sales" value={fmtUnits(summary.monthly_units)} />
            <Box sx={{ gridColumn: "1 / -1", display: "flex", gap: 1.5, alignItems: "center" }}>
              <RiskProfileBar row={summary} ui={ui} width={120} />
              <RiskCounts row={summary} ui={ui} fontSize={12} />
            </Box>
          </Box>
          <Typography sx={{ fontSize: 11.5, color: colors.textTertiary, mt: 1, lineHeight: 1.5 }}>
            Days cover pools all {fmtUnits(summary.skus)} active SKUs; revenue at risk is the
            30-day revenue of its Critical and At Risk SKUs.
          </Typography>

          <Typography sx={{ fontSize: 14, fontWeight: 700, color: colors.textPrimary, mt: 2.5 }}>
            SKUs
          </Typography>
          <Typography sx={{ fontSize: 12, color: colors.textSecondary, mb: 1 }}>
            Worst first. Select a SKU to see its inventory and sales history.
          </Typography>

          {detail.loading ? (
            <Inline ui={ui}>
              <CircularProgress size={14} /> Loading SKUs…
            </Inline>
          ) : detail.error ? (
            <Inline ui={ui} tone={ui.danger}>
              {detail.error}
            </Inline>
          ) : (
            <Box sx={{ ...ui.card, overflow: "hidden" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {["SKU", "On Hand", "30d Sales", "Days Cover", "Risk"].map((label, index) => (
                      <TableCell
                        key={label}
                        sx={headCellSx(ui, {
                          textAlign: index > 0 && index < 4 ? "right" : "left",
                          whiteSpace: "nowrap",
                          ...(index === 0 ? { pl: 2 } : {}),
                        })}
                      >
                        {label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {skus.map((row) => {
                    const selected = row.sku === selectedSku;
                    return (
                      <TableRow
                        key={row.sku}
                        hover
                        selected={selected}
                        aria-selected={selected}
                        tabIndex={0}
                        onClick={() => setSelectedSku(selected ? null : row.sku)}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            setSelectedSku(selected ? null : row.sku);
                          }
                        }}
                        sx={{
                          cursor: "pointer",
                          "&.Mui-selected, &.Mui-selected:hover": { backgroundColor: ui.accentSoft },
                          "&.Mui-selected > td:first-of-type": {
                            boxShadow: `inset 3px 0 0 ${ui.accent}`,
                          },
                        }}
                      >
                        <TableCell
                          sx={textCellSx(ui, { pl: 2, fontFamily: MONO_FONT, fontSize: 12.5 })}
                        >
                          {row.sku}
                        </TableCell>
                        <TableCell sx={numCellSx(ui, { fontFamily: MONO_FONT })}>
                          {fmtUnits(row.on_hand)}
                        </TableCell>
                        <TableCell sx={numCellSx(ui, { fontFamily: MONO_FONT })}>
                          {fmtUnits(row.units)}
                        </TableCell>
                        <TableCell sx={numCellSx(ui, { fontFamily: MONO_FONT })}>
                          {row.days_cover == null ? (
                            <Box component="span" sx={{ color: colors.textTertiary }}>
                              —
                            </Box>
                          ) : (
                            fmtDays(row.days_cover)
                          )}
                        </TableCell>
                        <TableCell sx={textCellSx(ui, { fontSize: 12.5 })}>
                          <RiskLabel segment={RISK_BY_KEY[row.risk]} ui={ui} />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              {hidden ? (
                <Typography
                  sx={{
                    fontSize: 12,
                    color: colors.textTertiary,
                    px: 2,
                    py: 1,
                    borderTop: `1px solid ${colors.border}`,
                  }}
                >
                  + {fmtUnits(hidden)} inactive SKU{hidden === 1 ? "" : "s"} hidden (no stock and no
                  sales)
                </Typography>
              ) : null}
            </Box>
          )}

          {selectedSku ? (
            <>
              {/* Stays mounted across SKU switches; only its body swaps between loading and chart. */}
              <Box
                role="region"
                aria-label="SKU history"
                sx={{
                  mt: 1,
                  pt: 1.5,
                  ...(pinHistory
                    ? {
                        position: "sticky",
                        bottom: 0,
                        zIndex: 2,
                        mx: mobile ? -2 : -3,
                        px: mobile ? 2 : 3,
                        pb: 1.5,
                        backgroundColor: colors.bgPaper,
                        borderTop: `1px solid ${historyStuck ? colors.border : "transparent"}`,
                        boxShadow: historyStuck ? "0 -6px 16px rgba(15, 23, 42, 0.08)" : "none",
                        transition: "box-shadow 120ms ease, border-color 120ms ease",
                      }
                    : {}),
                }}
              >
                <Box sx={{ ...ui.card, p: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }} aria-live="polite">
                      <Typography sx={{ fontSize: 14, fontWeight: 700, color: colors.textPrimary }}>
                        Inventory &amp; Daily Sales
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: colors.textSecondary }}>
                        <Box component="span" sx={{ fontFamily: MONO_FONT, fontWeight: 600 }}>
                          {selectedSku}
                        </Box>{" "}
                        · On-hand inventory and units sold per day
                      </Typography>
                    </Box>
                    <IconButton
                      size="small"
                      onClick={() => setSelectedSku(null)}
                      aria-label="Close history"
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      flexWrap: "wrap",
                      mt: 1,
                    }}
                  >
                    <PillTabs tabs={HISTORY_DAY_TABS} value={days} onChange={setDays} />
                    <Typography sx={{ fontSize: 12, color: colors.textSecondary }}>
                      {history.data
                        ? `${longDay(history.data.window.start)} → ${longDay(history.data.window.end)}`
                        : "\u00a0"}
                    </Typography>
                  </Box>
                  {/* Reserve the chart's height while loading so the panel does not jump. */}
                  <Box sx={{ minHeight: chartHeight + 40 }}>
                    {history.loading ? (
                      <Inline ui={ui}>
                        <CircularProgress size={14} /> Loading history…
                      </Inline>
                    ) : history.error ? (
                      <Inline ui={ui} tone={ui.danger}>
                        {history.error}
                      </Inline>
                    ) : history.data ? (
                      <SkuHistory
                        history={history.data}
                        ui={ui}
                        chartHeight={chartHeight}
                        liveOnHand={selectedOnHand}
                      />
                    ) : null}
                  </Box>
                </Box>
              </Box>
              {/* "1px", not 1: in sx a number <= 1 is a fraction, so 1 would mean 100% of the drawer. */}
              <Box ref={stuckSentinel} sx={{ height: "1px" }} />
            </>
          ) : null}

          {/* Below the SKU table and its history panel, so it never pushes either out of view. */}
          <ProductPicture
            images={detail.data?.product_images}
            name={summary.product_name}
            ui={ui}
          />
        </Box>
      ) : null}
    </Drawer>
  );
};

export default ProductRiskDrawer;
